import React, { useState, useEffect, useRef } from 'react';
import { BookOpen, Settings, Bookmark, BookmarkCheck, Share2, Download, Maximize2, Minimize2, Play, Pause, Volume2, Eye, Minus, Plus, X, Save, MessageSquare, Heart, Headphones, Highlighter, Brain, BarChart3, Link, Focus, Search, Users, Star, Trophy, Award, Clock, Zap, Image, Video, Keyboard, Languages, Cloud, Bell } from 'lucide-react';
import TextToSpeech from './TextToSpeech';
import EnhancedAudioPlayer from './EnhancedAudioPlayer';
import SmartHighlighting from './SmartHighlighting';
import InlineAnnotations from './InlineAnnotations';
import AIArticleInsights from './AIArticleInsights';
import ReadingAnalytics from './ReadingAnalytics';
import SmartLinks from './SmartLinks';
import FocusMode from './FocusMode';
import ContextSearch from './ContextSearch';
import RelatedContent from './RelatedContent';
import ReadingGroups from './ReadingGroups';
import InlineComments from './InlineComments';
import CommunityHighlights from './CommunityHighlights';
import ReadingChallenges from './ReadingChallenges';
import AchievementSystem from './AchievementSystem';
import LearningMode from './LearningMode';
import SmartScheduling from './SmartScheduling';
import ReadingModes from './ReadingModes';
import ImageEnhancement from './ImageEnhancement';
import DataVisualization from './DataVisualization';
import MediaIntegration from './MediaIntegration';
import OfflineReading from './OfflineReading';
import AccessibilityFeatures from './AccessibilityFeatures';
import KeyboardShortcuts from './KeyboardShortcuts';
import MultiLanguageSupport from './MultiLanguageSupport';
import DyslexiaSupport from './DyslexiaSupport';
import CrossDeviceSync from './CrossDeviceSync';
import AutoRefresh from './AutoRefresh';

interface Article {
  id: string;
  title: string;
  content: string;
  author: string;
  publishedAt: Date;
  readTime: number;
  tags: string[];
  imageUrl?: string;
  likes?: number;
  isLiked?: boolean;
  isBookmarked?: boolean;
  isStarred?: boolean;
  isFlagged?: boolean;
  category?: string;
}

interface ReadingSettings {
  fontSize: 'small' | 'medium' | 'large' | 'xlarge';
  lineHeight: 'tight' | 'normal' | 'relaxed';
  fontFamily: 'serif' | 'sans' | 'mono';
  theme: 'light' | 'dark' | 'sepia';
  width: 'narrow' | 'medium' | 'wide';
  autoScroll: boolean;
  autoScrollSpeed: number;
}

interface EnhancedArticleReaderProps {
  article: Article;
  onClose: () => void;
  onBookmark: (articleId: string, bookmarked: boolean) => void;
  onShare: (articleId: string) => void;
  onDownload: (articleId: string) => void;
  isBookmarked?: boolean;
}

const EnhancedArticleReader: React.FC<EnhancedArticleReaderProps> = ({
  article,
  onClose,
  onBookmark,
  onShare,
  onDownload,
  isBookmarked = false
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [notes, setNotes] = useState<string>('');
  const [showNotes, setShowNotes] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isFlagged, setIsFlagged] = useState(false);
  const [isTTSEnabled, setIsTTSEnabled] = useState(false);
  const [ttsSpeed, setTtsSpeed] = useState(1);
  const [selectedVoice, setSelectedVoice] = useState<SpeechSynthesisVoice | null>(null);
  const [showAudioPlayer, setShowAudioPlayer] = useState(false);
  const [audioTrack, setAudioTrack] = useState<any>(null);
  const [showHighlights, setShowHighlights] = useState(true);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [showAIInsights, setShowAIInsights] = useState(true);
  const [showReadingAnalytics, setShowReadingAnalytics] = useState(true);
  const [showSmartLinks, setShowSmartLinks] = useState(true);
  const [showFocusMode, setShowFocusMode] = useState(false);
  const [showContextSearch, setShowContextSearch] = useState(true);
  const [showRelatedContent, setShowRelatedContent] = useState(true);
  const [showReadingGroups, setShowReadingGroups] = useState(false);
  const [showInlineComments, setShowInlineComments] = useState(true);
  const [showCommunityHighlights, setShowCommunityHighlights] = useState(true);
  const [showReadingChallenges, setShowReadingChallenges] = useState(false);
  const [showAchievementSystem, setShowAchievementSystem] = useState(false);
  const [showLearningMode, setShowLearningMode] = useState(false);
  const [showSmartScheduling, setShowSmartScheduling] = useState(false);
  const [showReadingModes, setShowReadingModes] = useState(false);
  const [showImageEnhancement, setShowImageEnhancement] = useState(false);
  const [showDataVisualization, setShowDataVisualization] = useState(false);
  const [showMediaIntegration, setShowMediaIntegration] = useState(false);
  const [showOfflineReading, setShowOfflineReading] = useState(false);
  const [showAccessibilityFeatures, setShowAccessibilityFeatures] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [showMultiLanguageSupport, setShowMultiLanguageSupport] = useState(false);
  const [showDyslexiaSupport, setShowDyslexiaSupport] = useState(false);
  const [showCrossDeviceSync, setShowCrossDeviceSync] = useState(false);
  const [showAutoRefresh, setShowAutoRefresh] = useState(false);

  const [settings, setSettings] = useState<ReadingSettings>({
    fontSize: 'medium',
    lineHeight: 'normal',
    fontFamily: 'serif',
    theme: 'light',
    width: 'medium',
    autoScroll: false,
    autoScrollSpeed: 1
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef<number | null>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-scroll functionality
  useEffect(() => {
    if (settings.autoScroll && isPlaying) {
      autoScrollRef.current = window.setInterval(() => {
        if (contentRef.current) {
          const scrollAmount = settings.autoScrollSpeed * 0.5;
          contentRef.current.scrollTop += scrollAmount;
        }
      }, 16);
    } else {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
        autoScrollRef.current = null;
      }
    }

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [settings.autoScroll, isPlaying, settings.autoScrollSpeed]);

  // Reading progress tracking
  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const element = contentRef.current;
        const scrollTop = element.scrollTop;
        const scrollHeight = element.scrollHeight - element.clientHeight;
        const progress = (scrollTop / scrollHeight) * 100;
        setReadingProgress(Math.min(100, Math.max(0, progress)));
      }
    };

    const element = contentRef.current;
    if (element) {
      element.addEventListener('scroll', handleScroll);
      return () => element.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Ensure body can scroll when reader is open
  useEffect(() => {
    document.body.style.overflow = 'unset';
    return () => {
      // Reset to default when component unmounts
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Auto-hide controls
  useEffect(() => {
    const handleMouseMove = () => {
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    };

    if (isFullscreen) {
      document.addEventListener('mousemove', handleMouseMove);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        if (controlsTimeoutRef.current) {
          clearTimeout(controlsTimeoutRef.current);
        }
      };
    }
  }, [isFullscreen]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const adjustScrollSpeed = (direction: 'up' | 'down') => {
    const newSpeed = direction === 'up' 
      ? Math.min(5, settings.autoScrollSpeed + 0.5)
      : Math.max(0.5, settings.autoScrollSpeed - 0.5);
    
    setSettings(prev => ({ ...prev, autoScrollSpeed: newSpeed }));
  };

  const updateSetting = <K extends keyof ReadingSettings>(
    key: K, 
    value: ReadingSettings[K]
  ) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const getFontSize = () => {
    const sizes = { small: '14px', medium: '16px', large: '18px', xlarge: '20px' };
    return sizes[settings.fontSize];
  };

  const getLineHeight = () => {
    const heights = { tight: '1.4', normal: '1.6', relaxed: '1.8' };
    return heights[settings.lineHeight];
  };

  const getFontFamily = () => {
    const families = {
      serif: 'Georgia, "Times New Roman", serif',
      sans: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
      mono: '"JetBrains Mono", "Fira Code", monospace'
    };
    return families[settings.fontFamily];
  };

  const getThemeClasses = () => {
    const themes = {
      light: 'bg-white text-gray-900',
      dark: 'bg-gray-900 text-gray-100',
      sepia: 'bg-amber-50 text-amber-900'
    };
    return themes[settings.theme];
  };

  const getWidthClass = () => {
    const widths = {
      narrow: 'max-w-2xl',
      medium: 'max-w-4xl',
      wide: 'max-w-6xl'
    };
    return widths[settings.width];
  };

  return (
    <div className={`fixed inset-0 z-50 ${getThemeClasses()} transition-colors duration-300 flex flex-col`}>
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 bg-blue-500 z-50">
        <div 
          className="h-full bg-blue-600 transition-all duration-300"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      {/* Header */}
      <div className={`fixed top-0 left-0 right-0 z-40 transition-transform duration-300 ${
        showControls ? 'translate-y-0' : '-translate-y-full'
      }`}>
        <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
                <div>
                  <h1 className="text-lg font-semibold truncate max-w-md">{article.title}</h1>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    by {article.author} • {article.readTime} min read
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => onBookmark(article.id, !isBookmarked)}
                  className={`p-2 rounded-lg transition-colors ${
                    isBookmarked 
                      ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {isBookmarked ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
                </button>

                <button
                  onClick={() => onShare(article.id)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <Share2 className="w-5 h-5" />
                </button>

                <button
                  onClick={() => onDownload(article.id)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <Download className="w-5 h-5" />
                </button>

                <button
                  onClick={() => {
                    // Mock audio track for demonstration
                    const mockTrack = {
                      id: article.id,
                      title: article.title,
                      author: 'Author Name',
                      duration: 300, // 5 minutes
                      url: 'https://example.com/audio.mp3',
                      waveform: Array.from({ length: 100 }, () => Math.random() * 0.8 + 0.1),
                      isLiked: false,
                      isBookmarked: false,
                      playCount: 0,
                      createdAt: new Date(),
                      tags: ['narration', 'article']
                    };
                    setAudioTrack(mockTrack);
                    setShowAudioPlayer(true);
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <Headphones className="w-5 h-5" />
                </button>

                <button
                  onClick={toggleFullscreen}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
                </button>

                <button
                  onClick={() => setIsSettingsOpen(true)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <Settings className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto pt-16 pb-20" style={{ minHeight: 0 }}>
          <div className={`mx-auto px-4 ${getWidthClass()}`}>
            <div
              ref={contentRef}
              className="prose prose-lg max-w-none"
              style={{
                fontSize: getFontSize(),
                lineHeight: getLineHeight(),
                fontFamily: getFontFamily(),
                minHeight: '100vh'
              }}
            >
              {article.imageUrl && (
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-64 object-cover rounded-lg mb-8"
                />
              )}

              <div dangerouslySetInnerHTML={{ __html: article.content }} />
            </div>
          </div>
        </div>

        {/* Smart Highlighting */}
        {showHighlights && (
          <SmartHighlighting
            contentRef={contentRef}
            userId="current-user"
            onHighlightCreate={(highlight) => () => {}}
            onHighlightUpdate={(highlight) => () => {}}
            onHighlightDelete={(highlightId) => () => {}}
            onHighlightShare={(highlight) => () => {}}
          />
        )}

        {/* Inline Annotations */}
        {showAnnotations && (
          <InlineAnnotations
            contentRef={contentRef}
            userId="current-user"
            onAnnotationCreate={(annotation) => () => {}}
            onAnnotationUpdate={(annotation) => () => {}}
            onAnnotationDelete={(annotationId) => () => {}}
            onAnnotationReply={(annotationId, reply) => () => {}}
          />
        )}

        {/* AI Article Insights */}
        {showAIInsights && (
          <AIArticleInsights
            articleId={article.id}
            articleContent={article.content}
            articleTitle={article.title}
            onInsightCreate={(insight) => () => {}}
            onInsightUpdate={(insight) => () => {}}
            onInsightDelete={(insightId) => () => {}}
            onInsightShare={(insight) => () => {}}
          />
        )}

        {/* Reading Analytics */}
        {showReadingAnalytics && (
          <ReadingAnalytics
            userId="current-user"
            onInsightAction={(insight) => () => {}}
            onGoalUpdate={(goal) => () => {}}
          />
        )}

        {/* Smart Links */}
        {showSmartLinks && (
          <SmartLinks
            contentRef={contentRef}
            onLinkCreate={(link) => () => {}}
            onLinkUpdate={(link) => () => {}}
            onLinkDelete={(linkId) => () => {}}
            onLinkShare={(link) => () => {}}
          />
        )}

        {/* Focus Mode */}
        {showFocusMode && (
          <FocusMode
            onSessionStart={(session) => () => {}}
            onSessionEnd={(session) => () => {}}
            onSessionUpdate={(session) => () => {}}
          />
        )}

        {/* Context Search */}
        {showContextSearch && (
          <ContextSearch
            articleId={article.id}
            articleContent={article.content}
            articleTitle={article.title}
            onSearchResult={(result) => () => {}}
            onSearchQuery={(query) => () => {}}
          />
        )}

        {/* Related Content */}
        {showRelatedContent && (
          <RelatedContent
            articleId={article.id}
            articleTitle={article.title}
            articleTags={article.tags || []}
            articleCategory={article.category || 'General'}
            onArticleClick={(article) => () => {}}
            onTopicClick={(topic) => () => {}}
            onBookmark={(articleId, bookmarked) => () => {}}
            onLike={(articleId, liked) => () => {}}
          />
        )}

        {/* Reading Groups */}
        {showReadingGroups && (
          <ReadingGroups
            userId="current-user"
            onGroupJoin={(groupId) => () => {}}
            onGroupLeave={(groupId) => () => {}}
            onDiscussionCreate={(discussion) => () => {}}
            onCommentCreate={(comment) => () => {}}
          />
        )}

        {/* Inline Comments */}
        {showInlineComments && (
          <InlineComments
            articleId={article.id}
            contentRef={contentRef}
            onCommentCreate={(comment) => () => {}}
            onCommentUpdate={(comment) => () => {}}
            onCommentDelete={(commentId) => () => {}}
            onCommentReply={(commentId, reply) => () => {}}
            onCommentLike={(commentId, liked) => () => {}}
            onCommentPin={(commentId, pinned) => () => {}}
            onCommentResolve={(commentId, resolved) => () => {}}
          />
        )}

        {/* Community Highlights */}
        {showCommunityHighlights && (
          <CommunityHighlights
            articleId={article.id}
            onHighlightClick={(highlight) => () => {}}
            onHighlightLike={(highlightId, liked) => () => {}}
            onHighlightBookmark={(highlightId, bookmarked) => () => {}}
            onHighlightShare={(highlight) => () => {}}
          />
        )}

        {/* Reading Challenges */}
        {showReadingChallenges && (
          <ReadingChallenges
            userId="current-user"
            onChallengeJoin={(challengeId) => () => {}}
            onChallengeLeave={(challengeId) => () => {}}
            onAchievementUnlock={(achievement) => () => {}}
          />
        )}

        {/* Achievement System */}
        {showAchievementSystem && (
          <AchievementSystem
            userId="current-user"
            onAchievementUnlocked={(achievement) => () => {}}
            onPointsEarned={(points) => () => {}}
          />
        )}

        {/* Learning Mode */}
        {showLearningMode && (
          <LearningMode
            userId="current-user"
            articleId={article.id}
            content={article.content}
            onContentAdapt={(adaptedContent) => () => {}}
            onComprehensionCheck={(check) => () => {}}
            onLearningProgress={(progress) => () => {}}
          />
        )}

        {/* Smart Scheduling */}
        {showSmartScheduling && (
          <SmartScheduling
            userId="current-user"
            onSessionScheduled={(session) => () => {}}
            onReminderSet={(reminder) => () => {}}
            onOptimalTimeFound={(timeSlot) => () => {}}
          />
        )}

        {/* Reading Modes */}
        {showReadingModes && (
          <ReadingModes
            content={article.content}
            onModeChange={(mode) => () => {}}
            onSpeedChange={(speed) => () => {}}
            onProgressUpdate={(progress) => () => {}}
          />
        )}

        {/* Image Enhancement */}
        {showImageEnhancement && (
          <ImageEnhancement
            imageUrl={article.imageUrl || '/placeholder-image.jpg'}
            imageAlt={article.title}
            onAnnotationAdd={(annotation) => () => {}}
            onAnnotationUpdate={(annotation) => () => {}}
            onAnnotationDelete={(id) => () => {}}
            onImageDownload={(url) => () => {}}
            onImageShare={(url) => () => {}}
          />
        )}

        {/* Data Visualization */}
        {showDataVisualization && (
          <DataVisualization
            articleId={article.id}
            onChartCreate={(chart) => () => {}}
            onChartUpdate={(chart) => () => {}}
            onChartDelete={(id) => () => {}}
            onChartShare={(chart) => () => {}}
            onChartExport={(chart, format) => () => {}}
          />
        )}

        {/* Media Integration */}
        {showMediaIntegration && (
          <MediaIntegration
            articleId={article.id}
            onMediaAdd={(media) => () => {}}
            onMediaUpdate={(media) => () => {}}
            onMediaDelete={(id) => () => {}}
            onMediaPlay={(media) => () => {}}
            onMediaPause={(media) => () => {}}
            onMediaDownload={(media) => () => {}}
            onMediaShare={(media) => () => {}}
          />
        )}

        {/* Offline Reading */}
        {showOfflineReading && (
          <OfflineReading
            userId="current-user"
            onArticleDownload={(article) => () => {}}
            onArticleDelete={(id) => () => {}}
            onSyncRequest={(id) => () => {}}
            onStorageInfo={(info) => () => {}}
          />
        )}

        {/* Accessibility Features */}
        {showAccessibilityFeatures && (
          <AccessibilityFeatures
            onSettingsChange={(settings) => () => {}}
            onVoiceCommand={(command) => () => {}}
            onFocusChange={(element) => () => {}}
          />
        )}

        {/* Keyboard Shortcuts */}
        {showKeyboardShortcuts && (
          <KeyboardShortcuts
            onShortcutTrigger={(shortcut) => () => {}}
            onShortcutCustomize={(shortcut, newKey, newModifiers) => () => {}}
            onShortcutToggle={(shortcutId, enabled) => () => {}}
          />
        )}

        {/* Multi-Language Support */}
        {showMultiLanguageSupport && (
          <MultiLanguageSupport
            onLanguageChange={(language) => () => {}}
            onTranslationRequest={(text, targetLanguage) => () => {}}
            onTranslationUpdate={(translation) => () => {}}
            onLanguageDownload={(language) => () => {}}
            onLanguageDelete={(language) => () => {}}
          />
        )}

        {/* Dyslexia Support */}
        {showDyslexiaSupport && (
          <DyslexiaSupport
            onSettingsChange={(settings) => () => {}}
            onFontLoad={(fontFamily) => () => {}}
            onReadingTest={(testType) => () => {}}
          />
        )}

        {/* Cross-Device Sync */}
        {showCrossDeviceSync && (
          <CrossDeviceSync
            onSyncRequest={(deviceId) => () => {}}
            onDataSync={(data) => () => {}}
            onDeviceConnect={(device) => () => {}}
            onDeviceDisconnect={(deviceId) => () => {}}
          />
        )}

        {/* Auto-Refresh */}
        {showAutoRefresh && (
          <AutoRefresh
            onArticleUpdate={(article) => () => {}}
            onNotificationRequest={(title, body) => () => {}}
            onSettingsChange={(settings) => () => {}}
          />
        )}

      {/* TTS Panel */}
      {isTTSEnabled && (
        <div className="fixed bottom-20 left-4 right-4 z-30">
          <TextToSpeech
            text={`${article.title}. ${article.content.replace(/<[^>]*>/g, '')}`}
            isPlaying={isPlaying}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onStop={() => setIsPlaying(false)}
            onSpeedChange={setTtsSpeed}
            onVoiceChange={setSelectedVoice}
            className="shadow-lg"
          />
        </div>
      )}

      {/* Audio Player */}
      {showAudioPlayer && audioTrack && (
        <div className="fixed bottom-20 left-4 right-4 z-30">
          <EnhancedAudioPlayer
            track={audioTrack}
            onClose={() => setShowAudioPlayer(false)}
            onLike={(trackId, liked) => {
              }}
            onBookmark={(trackId, bookmarked) => {
              }}
            onShare={(trackId) => {
              }}
            onDownload={(trackId) => {
              }}
            className="shadow-lg"
          />
        </div>
      )}

      {/* Bottom Controls */}
      <div className={`fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300 ${
        showControls ? 'translate-y-0' : 'translate-y-full'
      }`}>
        <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setIsTTSEnabled(!isTTSEnabled)}
                  className={`p-2 rounded-lg transition-colors ${
                    isTTSEnabled 
                      ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Volume2 className="w-5 h-5" />
                </button>

                <button
                  onClick={togglePlayPause}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  disabled={!isTTSEnabled}
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                </button>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => adjustScrollSpeed('down')}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[3rem] text-center">
                    {settings.autoScrollSpeed}x
                  </span>
                  <button
                    onClick={() => adjustScrollSpeed('up')}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>

                <button
                  onClick={() => setIsLiked(!isLiked)}
                  className={`p-2 rounded-lg transition-colors ${
                    isLiked 
                      ? 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
                </button>

                <button
                  onClick={() => setShowNotes(!showNotes)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <MessageSquare className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowHighlights(!showHighlights)}
                  className={`p-2 rounded-lg transition-colors ${
                    showHighlights 
                      ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Highlighter className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowAnnotations(!showAnnotations)}
                  className={`p-2 rounded-lg transition-colors ${
                    showAnnotations 
                      ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <MessageSquare className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowAIInsights(!showAIInsights)}
                  className={`p-2 rounded-lg transition-colors ${
                    showAIInsights 
                      ? 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Brain className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowReadingAnalytics(!showReadingAnalytics)}
                  className={`p-2 rounded-lg transition-colors ${
                    showReadingAnalytics 
                      ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <BarChart3 className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowSmartLinks(!showSmartLinks)}
                  className={`p-2 rounded-lg transition-colors ${
                    showSmartLinks 
                      ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Link className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowFocusMode(!showFocusMode)}
                  className={`p-2 rounded-lg transition-colors ${
                    showFocusMode 
                      ? 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Focus className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowContextSearch(!showContextSearch)}
                  className={`p-2 rounded-lg transition-colors ${
                    showContextSearch 
                      ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900 dark:text-indigo-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Search className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowRelatedContent(!showRelatedContent)}
                  className={`p-2 rounded-lg transition-colors ${
                    showRelatedContent 
                      ? 'bg-teal-100 text-teal-600 dark:bg-teal-900 dark:text-teal-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <BookOpen className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowReadingGroups(!showReadingGroups)}
                  className={`p-2 rounded-lg transition-colors ${
                    showReadingGroups 
                      ? 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Users className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowInlineComments(!showInlineComments)}
                  className={`p-2 rounded-lg transition-colors ${
                    showInlineComments 
                      ? 'bg-pink-100 text-pink-600 dark:bg-pink-900 dark:text-pink-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <MessageSquare className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowCommunityHighlights(!showCommunityHighlights)}
                  className={`p-2 rounded-lg transition-colors ${
                    showCommunityHighlights 
                      ? 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900 dark:text-cyan-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Star className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowReadingChallenges(!showReadingChallenges)}
                  className={`p-2 rounded-lg transition-colors ${
                    showReadingChallenges 
                      ? 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900 dark:text-yellow-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Trophy className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowAchievementSystem(!showAchievementSystem)}
                  className={`p-2 rounded-lg transition-colors ${
                    showAchievementSystem 
                      ? 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Award className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowLearningMode(!showLearningMode)}
                  className={`p-2 rounded-lg transition-colors ${
                    showLearningMode 
                      ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900 dark:text-indigo-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Brain className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowSmartScheduling(!showSmartScheduling)}
                  className={`p-2 rounded-lg transition-colors ${
                    showSmartScheduling 
                      ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900 dark:text-emerald-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Clock className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowReadingModes(!showReadingModes)}
                  className={`p-2 rounded-lg transition-colors ${
                    showReadingModes 
                      ? 'bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Zap className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowImageEnhancement(!showImageEnhancement)}
                  className={`p-2 rounded-lg transition-colors ${
                    showImageEnhancement 
                      ? 'bg-pink-100 text-pink-600 dark:bg-pink-900 dark:text-pink-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Image className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowDataVisualization(!showDataVisualization)}
                  className={`p-2 rounded-lg transition-colors ${
                    showDataVisualization 
                      ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900 dark:text-indigo-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <BarChart3 className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowMediaIntegration(!showMediaIntegration)}
                  className={`p-2 rounded-lg transition-colors ${
                    showMediaIntegration 
                      ? 'bg-teal-100 text-teal-600 dark:bg-teal-900 dark:text-teal-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Video className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowOfflineReading(!showOfflineReading)}
                  className={`p-2 rounded-lg transition-colors ${
                    showOfflineReading 
                      ? 'bg-amber-100 text-amber-600 dark:bg-amber-900 dark:text-amber-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Download className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowAccessibilityFeatures(!showAccessibilityFeatures)}
                  className={`p-2 rounded-lg transition-colors ${
                    showAccessibilityFeatures 
                      ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900 dark:text-emerald-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Eye className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowKeyboardShortcuts(!showKeyboardShortcuts)}
                  className={`p-2 rounded-lg transition-colors ${
                    showKeyboardShortcuts 
                      ? 'bg-violet-100 text-violet-600 dark:bg-violet-900 dark:text-violet-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Keyboard className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowMultiLanguageSupport(!showMultiLanguageSupport)}
                  className={`p-2 rounded-lg transition-colors ${
                    showMultiLanguageSupport 
                      ? 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900 dark:text-cyan-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Languages className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowDyslexiaSupport(!showDyslexiaSupport)}
                  className={`p-2 rounded-lg transition-colors ${
                    showDyslexiaSupport 
                      ? 'bg-pink-100 text-pink-600 dark:bg-pink-900 dark:text-pink-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <BookOpen className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowCrossDeviceSync(!showCrossDeviceSync)}
                  className={`p-2 rounded-lg transition-colors ${
                    showCrossDeviceSync 
                      ? 'bg-slate-100 text-slate-600 dark:bg-slate-900 dark:text-slate-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Cloud className="w-5 h-5" />
                </button>

                <button
                  onClick={() => setShowAutoRefresh(!showAutoRefresh)}
                  className={`p-2 rounded-lg transition-colors ${
                    showAutoRefresh 
                      ? 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-400' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <Bell className="w-5 h-5" />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {Math.round(readingProgress)}% read
                </span>
                <div className="w-32 h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                  <div 
                    className="h-full bg-blue-600 rounded-full transition-all duration-300"
                    style={{ width: `${readingProgress}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {isSettingsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Reading Settings</h3>
              <button
                onClick={() => setIsSettingsOpen(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Font Size */}
              <div>
                <label className="block text-sm font-medium mb-2">Font Size</label>
                <div className="flex gap-2">
                  {(['small', 'medium', 'large', 'xlarge'] as const).map((size) => (
                    <button
                      key={size}
                      onClick={() => updateSetting('fontSize', size)}
                      className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                        settings.fontSize === size
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {size.charAt(0).toUpperCase() + size.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Line Height */}
              <div>
                <label className="block text-sm font-medium mb-2">Line Height</label>
                <div className="flex gap-2">
                  {(['tight', 'normal', 'relaxed'] as const).map((height) => (
                    <button
                      key={height}
                      onClick={() => updateSetting('lineHeight', height)}
                      className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                        settings.lineHeight === height
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {height.charAt(0).toUpperCase() + height.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Font Family */}
              <div>
                <label className="block text-sm font-medium mb-2">Font Family</label>
                <div className="flex gap-2">
                  {(['serif', 'sans', 'mono'] as const).map((family) => (
                    <button
                      key={family}
                      onClick={() => updateSetting('fontFamily', family)}
                      className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                        settings.fontFamily === family
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {family.charAt(0).toUpperCase() + family.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Theme */}
              <div>
                <label className="block text-sm font-medium mb-2">Theme</label>
                <div className="flex gap-2">
                  {(['light', 'dark', 'sepia'] as const).map((theme) => (
                    <button
                      key={theme}
                      onClick={() => updateSetting('theme', theme)}
                      className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                        settings.theme === theme
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {theme.charAt(0).toUpperCase() + theme.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Width */}
              <div>
                <label className="block text-sm font-medium mb-2">Content Width</label>
                <div className="flex gap-2">
                  {(['narrow', 'medium', 'wide'] as const).map((width) => (
                    <button
                      key={width}
                      onClick={() => updateSetting('width', width)}
                      className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                        settings.width === width
                          ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      {width.charAt(0).toUpperCase() + width.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Auto-scroll */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Auto-scroll</label>
                <button
                  onClick={() => updateSetting('autoScroll', !settings.autoScroll)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    settings.autoScroll ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      settings.autoScroll ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notes Panel */}
      {showNotes && (
        <div className="fixed right-0 top-0 bottom-0 w-80 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 z-40">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Notes</h3>
              <button
                onClick={() => setShowNotes(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="p-4">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add your notes here..."
              className="w-full h-64 p-3 border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
            <button
              onClick={() => {/* Save notes */}}
              className="mt-4 w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Save className="w-4 h-4 inline mr-2" />
              Save Notes
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedArticleReader;
